import { AlertTriangle, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';

export function PageErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const { t: tCommon } = useTranslation('common');
  const t = (key: string, opts?: Record<string, unknown>) =>
    tCommon(`errorPage.${key}`, opts);

  let title = t('genericTitle');
  let message = t('genericMessage');
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      title = t('notFoundTitle');
      message = t('notFoundMessage');
    } else if (error.status === 403) {
      title = t('accessDeniedTitle');
      message = t('accessDeniedMessage');
    } else {
      title = t('errorWithStatus', { status: error.status });
      message = error.statusText || message;
    }
  } else if (import.meta.env.DEV && error instanceof Error) {
    message = error.message;
    stack = error.stack;
  }

  const handleRetry = () => {
    navigate(0);
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-8">
      <div className="flex max-w-md flex-col items-center text-center">
        <div className="mb-4 rounded-full bg-destructive/10 p-3">
          <AlertTriangle className="size-6 text-destructive" />
        </div>
        <h2 className="mb-2 text-xl font-semibold text-foreground">{title}</h2>
        <p className="mb-6 text-sm text-muted-foreground">{message}</p>

        {stack && (
          <pre className="mb-6 max-h-48 w-full overflow-auto rounded-lg bg-muted p-4 text-left text-xs">
            <code>{stack}</code>
          </pre>
        )}

        <div className="flex gap-3">
          <Button variant="outline" onClick={handleGoBack}>
            {tCommon('actions.goBack')}
          </Button>
          <Button onClick={handleRetry}>
            <RefreshCw className="size-4" />
            {tCommon('actions.tryAgain')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PageErrorBoundary;
